(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const reveal = (element) => element?.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'nearest' });
  const focusAndReveal = (element) => {
    if (!element) return;
    if (!element.hasAttribute('tabindex')) element.setAttribute('tabindex', '-1');
    element.focus({ preventScroll: true });
    reveal(element);
  };

  const parseData = (root, selector) => {
    const node = root.querySelector(selector);
    if (!node) return [];

    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return [];
    }
  };

  const getHighPreference = (value) => ['high', 'yes', 'daily', 'always', 'full'].includes(value);

  const initFinder = (root) => {
    if (root.dataset.itsPlannerFinderReady === 'true') return;
    root.dataset.itsPlannerFinderReady = 'true';

    const planners = parseData(root, '[data-planner-options]');
    const questions = [...root.querySelectorAll('[data-finder-question]')];
    const nextButton = root.querySelector('[data-finder-next]');
    const backButton = root.querySelector('[data-finder-back]');
    const resetButton = root.querySelector('[data-finder-reset]');
    const result = root.querySelector('[data-finder-result]');
    const resultTitle = root.querySelector('[data-finder-result-title]');
    const resultText = root.querySelector('[data-finder-result-text]');
    const resultLink = root.querySelector('[data-finder-result-link]');
    const resultImage = root.querySelector('[data-finder-result-image]');
    const progress = root.querySelector('[data-finder-progress]');
    const emptyState = root.querySelector('[data-finder-empty]');
    const answers = {};
    let current = 0;

    const focusCurrentQuestion = () => {
      const question = questions[current];
      focusAndReveal(question?.querySelector('legend') || question);
    };

    if (!planners.length) {
      emptyState?.removeAttribute('hidden');
      nextButton?.setAttribute('disabled', 'disabled');
      return;
    }

    const update = () => {
      questions.forEach((question, index) => {
        question.hidden = index !== current;
      });

      const questionKey = questions[current]?.dataset.question;
      const answered = Boolean(answers[questionKey]);
      if (nextButton) nextButton.disabled = !answered;
      if (backButton) backButton.hidden = current === 0;
      if (nextButton) nextButton.textContent = current === questions.length - 1 ? 'Ver minha It’s' : 'Continuar';
      if (progress) progress.textContent = `${String(current + 1).padStart(2, '0')} / ${String(questions.length).padStart(2, '0')}`;
    };

    const renderResult = () => {
      const scored = planners.map((planner) => {
        const score = Object.entries(answers).reduce((total, [question, answer]) => {
          const field = {
            writing: 'writing',
            schedule: 'schedule',
            view: 'daily',
            portable: 'portable',
            detail: 'detail',
          }[question];
          const value = Number(planner.scores?.[field] ?? 2.5);
          return total + (getHighPreference(answer) ? value : 5 - value);
        }, 0);
        return { planner, score };
      }).sort((first, second) => second.score - first.score);

      const winner = scored[0]?.planner;
      if (!winner) return;

      if (resultTitle) resultTitle.textContent = winner.title;
      if (resultText) resultText.textContent = winner.profile || 'Um formato pensado para acompanhar a sua rotina.';
      if (resultLink) {
        if (winner.url) {
          resultLink.href = winner.url;
          resultLink.hidden = false;
        } else {
          resultLink.removeAttribute('href');
          resultLink.hidden = true;
        }
      }
      if (resultImage) {
        resultImage.src = winner.image || '';
        resultImage.alt = winner.title;
        resultImage.hidden = !winner.image;
      }
      result?.removeAttribute('hidden');
      focusAndReveal(resultTitle || result);
    };

    root.querySelectorAll('[data-finder-answer]').forEach((button) => {
      button.addEventListener('click', () => {
        const question = button.closest('[data-finder-question]')?.dataset.question;
        if (!question) return;
        answers[question] = button.dataset.value;
        button.closest('[data-finder-question]')?.querySelectorAll('[data-finder-answer]').forEach((option) => {
          option.setAttribute('aria-pressed', option === button ? 'true' : 'false');
        });
        update();
      });
    });

    nextButton?.addEventListener('click', () => {
      if (current < questions.length - 1) {
        current += 1;
        update();
        focusCurrentQuestion();
      } else {
        renderResult();
      }
    });

    backButton?.addEventListener('click', () => {
      if (current > 0) {
        current -= 1;
        update();
        focusCurrentQuestion();
      }
    });

    resetButton?.addEventListener('click', () => {
      Object.keys(answers).forEach((key) => delete answers[key]);
      root.querySelectorAll('[data-finder-answer]').forEach((button) => button.setAttribute('aria-pressed', 'false'));
      result?.setAttribute('hidden', 'hidden');
      current = 0;
      update();
      focusCurrentQuestion();
    });

    update();
  };

  const initComparator = (root) => {
    if (root.dataset.itsPlannerComparatorReady === 'true') return;
    root.dataset.itsPlannerComparatorReady = 'true';

    const planners = parseData(root, '[data-comparator-options]');
    const options = root.querySelector('[data-comparator-list]');
    const compareButton = root.querySelector('[data-comparator-submit]');
    const output = root.querySelector('[data-comparator-output]');
    const outputTitle = root.querySelector('[data-comparator-title]');
    const table = root.querySelector('[data-comparator-table]');
    const emptyState = root.querySelector('[data-comparator-empty]');
    const selected = new Set();
    const checkboxes = [];

    if (!planners.length) {
      emptyState?.removeAttribute('hidden');
      compareButton?.setAttribute('disabled', 'disabled');
      return;
    }

    const updateButton = () => {
      const reachedLimit = selected.size >= 3;
      checkboxes.forEach((checkbox) => {
        checkbox.disabled = reachedLimit && !checkbox.checked;
      });
      if (compareButton) compareButton.disabled = selected.size < 2;
      if (compareButton) {
        compareButton.textContent = selected.size < 2 ? 'Compare 2 planners' : `Comparar ${selected.size} planners`;
      }
    };

    planners.forEach((planner, index) => {
      const label = document.createElement('label');
      label.className = 'its-comparator__option';

      const checkbox = document.createElement('input');
      checkbox.type = 'checkbox';
      checkbox.value = String(index);
      checkbox.setAttribute('aria-label', `Selecionar ${planner.title}`);
      checkboxes.push(checkbox);

      const copy = document.createElement('span');
      copy.className = 'its-comparator__option-copy';
      const code = document.createElement('span');
      code.className = 'its-micro';
      code.textContent = planner.code || `0${index + 1}`;
      const title = document.createElement('strong');
      title.textContent = planner.title;
      copy.append(code, title);

      checkbox.addEventListener('change', () => {
        if (checkbox.checked) selected.add(index);
        else selected.delete(index);
        updateButton();
      });

      label.append(checkbox, copy);
      options?.appendChild(label);
    });

    compareButton?.addEventListener('click', () => {
      const chosen = [...selected].map((index) => planners[index]);
      if (chosen.length < 2 || chosen.length > 3 || !table) return;

      table.replaceChildren();
      const head = document.createElement('thead');
      const headRow = document.createElement('tr');
      const emptyHeader = document.createElement('th');
      emptyHeader.scope = 'col';
      emptyHeader.textContent = 'Critério';
      headRow.appendChild(emptyHeader);
      chosen.forEach((planner) => {
        const cell = document.createElement('th');
        cell.scope = 'col';
        cell.textContent = planner.title;
        headRow.appendChild(cell);
      });
      head.appendChild(headRow);

      const body = document.createElement('tbody');
      const rows = [
        ['Tamanho', 'size'],
        ['Visão', 'view'],
        ['Tipo', 'schedule'],
        ['Páginas', 'pages'],
        ['Papel', 'paper'],
        ['Personalização', 'personalization'],
        ['Perfil recomendado', 'profile'],
      ];
      rows.forEach(([label, key]) => {
        const row = document.createElement('tr');
        const labelCell = document.createElement('th');
        labelCell.scope = 'row';
        labelCell.textContent = label;
        row.appendChild(labelCell);
        chosen.forEach((planner) => {
          const cell = document.createElement('td');
          cell.textContent = planner.details?.[key] || '—';
          row.appendChild(cell);
        });
        body.appendChild(row);
      });

      table.append(head, body);
      if (outputTitle) outputTitle.textContent = `Comparando ${chosen.map((planner) => planner.title).join(' · ')}`;
      output?.removeAttribute('hidden');
      reveal(output);
    });

    updateButton();
  };

  const boot = (scope = document) => {
    scope.querySelectorAll('[data-its-planner-finder]').forEach(initFinder);
    scope.querySelectorAll('[data-its-planner-comparator]').forEach(initComparator);
    if (reducedMotion) {
      scope.querySelectorAll('.its-autoplay-video').forEach((video) => {
        video.removeAttribute('autoplay');
        video.pause();
      });
    }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot());
  else boot();

  document.addEventListener('shopify:section:load', (event) => boot(event.target));
})();
